/**
 * Particles: the tokens' trails, embers falling from cleared bits, shards of
 * a refusal, sparks of an impact, rings, and the dots of a thread.
 *
 * The pool is pure and allocation-free after construction (struct of arrays,
 * swap-remove on death), so a busy seal costs no garbage. Its `scale` is the
 * degrade ladder's third rung: at 25 % only every fourth ordinary spawn is
 * kept, while essential ones (a token's core) always are. The GL pass that
 * draws the packed array lives at the bottom; it is a thin adapter.
 */

import type { Rgb } from './palette';
import type { GlKit } from './gl';
import vert from '../shaders/particles.vert.glsl' with { type: 'text' };
import frag from '../shaders/particles.frag.glsl' with { type: 'text' };

/** Packed layout: x, y, radius, kind (+16 on the stage plate), r, g, b, a. */
export const P_FLOATS = 8;
export const PK = { dot: 0, trail: 1, shard: 2, ring: 3, spark: 4, cursor: 5 } as const;
export const STAGE = 16;
export const MAX_PARTICLES = 2000;
export const PARTICLE_UNIFORMS = ['u_camera', 'u_canvas'] as const;
export const PARTICLE_SOURCES = { vert, frag } as const;

export interface Spawn {
  x: number; y: number;
  vx?: number; vy?: number;        // units per second
  ax?: number; ay?: number;        // units per second²
  drag?: number;                   // fraction of velocity kept per second (1 = none lost)
  size: number; size1?: number;    // radius at birth and at death
  life: number;                    // ms
  delay?: number;                  // ms before it appears
  kind: number;                    // a PK value
  stage?: boolean;                 // on the plate (stage units) rather than the viewport
  rgb: Rgb; alpha?: number;
  /** out: fades as it ages; in-out: rises and falls; none: full to the end; edges: 120 ms in, still, 120 ms out (reduced motion). */
  fade?: 'out' | 'in-out' | 'none' | 'edges';
}

const FADE = { out: 0, 'in-out': 1, none: 2, edges: 3 } as const;
/** The fade at each end of an `edges` particle: §4.6's 120 ms fade. */
export const EDGE_FADE_MS = 120;

export class ParticlePool {
  readonly cap: number;
  private n = 0;
  private scaleAcc = 0;
  /** Share of ordinary spawns kept (the ladder's "particles at 25 %"). */
  scale = 1;
  private readonly f: Record<'x' | 'y' | 'vx' | 'vy' | 'ax' | 'ay' | 'drag' | 's0' | 's1' | 'life' | 'age' | 'delay' | 'kind' | 'r' | 'g' | 'b' | 'a' | 'fade', Float32Array>;

  constructor(cap = MAX_PARTICLES) {
    this.cap = cap;
    const mk = () => new Float32Array(cap);
    this.f = {
      x: mk(), y: mk(), vx: mk(), vy: mk(), ax: mk(), ay: mk(), drag: mk(), s0: mk(), s1: mk(),
      life: mk(), age: mk(), delay: mk(), kind: mk(), r: mk(), g: mk(), b: mk(), a: mk(), fade: mk(),
    };
  }

  get count(): number {
    return this.n;
  }

  /** Add one; false when the pool is full or the scale skipped it. */
  spawn(s: Spawn, essential = false): boolean {
    if (!essential && this.scale < 1) {
      this.scaleAcc += this.scale;
      if (this.scaleAcc < 1) return false;
      this.scaleAcc -= 1;
    }
    if (this.n >= this.cap) return false;
    const i = this.n++, f = this.f;
    f.x[i] = s.x; f.y[i] = s.y;
    f.vx[i] = s.vx ?? 0; f.vy[i] = s.vy ?? 0;
    f.ax[i] = s.ax ?? 0; f.ay[i] = s.ay ?? 0;
    f.drag[i] = s.drag ?? 1;
    f.s0[i] = s.size; f.s1[i] = s.size1 ?? s.size;
    f.life[i] = Math.max(1, s.life); f.age[i] = 0; f.delay[i] = s.delay ?? 0;
    f.kind[i] = s.kind + (s.stage ? STAGE : 0);
    f.r[i] = s.rgb[0]; f.g[i] = s.rgb[1]; f.b[i] = s.rgb[2]; f.a[i] = s.alpha ?? 1;
    f.fade[i] = FADE[s.fade ?? 'out'];
    return true;
  }

  /** Advance every particle by `dtMs`; the dead are swapped out. */
  update(dtMs: number): void {
    const f = this.f, dt = Math.max(0, dtMs) / 1000;
    let i = 0;
    while (i < this.n) {
      const delay = f.delay[i] as number;
      if (delay > 0) {
        f.delay[i] = delay - dtMs;
        i++;
        continue;
      }
      const age = (f.age[i] as number) + dtMs;
      if (age >= (f.life[i] as number)) {
        this.kill(i);
        continue;
      }
      f.age[i] = age;
      const keep = (f.drag[i] as number) === 1 ? 1 : (f.drag[i] as number) ** dt;
      f.vx[i] = ((f.vx[i] as number) + (f.ax[i] as number) * dt) * keep;
      f.vy[i] = ((f.vy[i] as number) + (f.ay[i] as number) * dt) * keep;
      f.x[i] = (f.x[i] as number) + (f.vx[i] as number) * dt;
      f.y[i] = (f.y[i] as number) + (f.vy[i] as number) * dt;
      i++;
    }
  }

  private kill(i: number): void {
    const last = --this.n, f = this.f;
    if (i === last) return;
    for (const k of Object.keys(f) as (keyof typeof f)[]) {
      const a = f[k];
      a[i] = a[last] as number;
    }
  }

  clear(): void {
    this.n = 0;
  }

  /** Pack the live particles into `out` from instance `start`; returns how many were written. */
  pack(out: Float32Array, start = 0): number {
    const f = this.f;
    let w = start;
    const max = Math.floor(out.length / P_FLOATS);
    for (let i = 0; i < this.n && w < max; i++) {
      if ((f.delay[i] as number) > 0) continue;
      const age = f.age[i] as number, life = f.life[i] as number;
      const t = age / life;
      const fade = f.fade[i] as number;
      const alpha = fade === 0 ? (1 - t) ** 1.5 : fade === 1 ? Math.sin(Math.PI * t)
        : fade === 3 ? Math.max(0, Math.min(1, age / EDGE_FADE_MS, (life - age) / EDGE_FADE_MS)) : 1;
      const o = w * P_FLOATS;
      out[o] = f.x[i] as number;
      out[o + 1] = f.y[i] as number;
      out[o + 2] = (f.s0[i] as number) + ((f.s1[i] as number) - (f.s0[i] as number)) * t;
      out[o + 3] = f.kind[i] as number;
      out[o + 4] = f.r[i] as number;
      out[o + 5] = f.g[i] as number;
      out[o + 6] = f.b[i] as number;
      out[o + 7] = (f.a[i] as number) * alpha;
      w++;
    }
    return w - start;
  }
}

/** Write one particle straight into a packed array (a token's core, a cursor). */
export const writeParticle = (out: Float32Array, i: number, x: number, y: number, radius: number, kind: number, rgb: Rgb, alpha: number): boolean => {
  const o = i * P_FLOATS;
  if (o + P_FLOATS > out.length) return false;
  out[o] = x; out[o + 1] = y; out[o + 2] = radius; out[o + 3] = kind;
  out[o + 4] = rgb[0]; out[o + 5] = rgb[1]; out[o + 6] = rgb[2]; out[o + 7] = alpha;
  return true;
};

// ---------- recipes ----------

/** A burst of `n` particles flying out from a point, evenly around with a little jitter. */
export const burst = (pool: ParticlePool, at: { x: number; y: number }, n: number, o: { speed: number; size: number; life: number; kind: number; rgb: Rgb; stage?: boolean; seed?: number; gravity?: number }): void => {
  let seed = o.seed ?? 1;
  const rnd = () => {
    seed = (Math.imul(seed, 1664525) + 1013904223) >>> 0;
    return seed / 4294967296;
  };
  for (let i = 0; i < n; i++) {
    const ang = ((i + rnd() * 0.6) / n) * Math.PI * 2;
    const sp = o.speed * (0.6 + 0.8 * rnd());
    pool.spawn({
      x: at.x, y: at.y, vx: Math.cos(ang) * sp, vy: Math.sin(ang) * sp, ay: o.gravity ?? 0, drag: 0.08,
      size: o.size * (0.7 + 0.6 * rnd()), size1: o.size * 0.3, life: o.life * (0.7 + 0.5 * rnd()),
      kind: o.kind, stage: o.stage, rgb: o.rgb,
    });
  }
};

// ---------- the GL pass ----------

export interface ParticlePass {
  draw(data: Float32Array, count: number, camera: Float32Array, additive: boolean): void;
  destroy(): void;
}

export const createParticlePass = (gl: WebGL2RenderingContext, kit: GlKit): ParticlePass => {
  const prog = kit.link(vert, frag, 'particles');
  const u = kit.uniforms(prog, PARTICLE_UNIFORMS);
  const vao = gl.createVertexArray();
  const buf = gl.createBuffer();
  let capacity = 0;
  gl.bindVertexArray(vao);
  kit.bindQuad(0);
  gl.bindBuffer(gl.ARRAY_BUFFER, buf);
  const stride = P_FLOATS * 4;
  gl.enableVertexAttribArray(1);
  gl.vertexAttribPointer(1, 4, gl.FLOAT, false, stride, 0);
  gl.vertexAttribDivisor(1, 1);
  gl.enableVertexAttribArray(2);
  gl.vertexAttribPointer(2, 4, gl.FLOAT, false, stride, 16);
  gl.vertexAttribDivisor(2, 1);
  gl.bindVertexArray(null);

  return {
    draw(data, count, camera, additive) {
      if (count <= 0) return;
      gl.useProgram(prog);
      gl.bindVertexArray(vao);
      gl.bindBuffer(gl.ARRAY_BUFFER, buf);
      const bytes = count * P_FLOATS;
      if (bytes > capacity) {
        capacity = Math.max(bytes, data.length);
        gl.bufferData(gl.ARRAY_BUFFER, capacity * 4, gl.DYNAMIC_DRAW);
      }
      gl.bufferSubData(gl.ARRAY_BUFFER, 0, data, 0, bytes);
      gl.uniformMatrix4fv(u.u_camera ?? null, false, camera.subarray(0, 16));
      gl.uniform4f(u.u_canvas ?? null, camera[20] ?? 0, camera[21] ?? 0, camera[16] ?? 1, camera[17] ?? 1);
      if (additive) gl.blendFunc(gl.ONE, gl.ONE); else gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
      gl.drawArraysInstanced(gl.TRIANGLE_STRIP, 0, 4, count);
      gl.bindVertexArray(null);
    },
    destroy() {
      gl.deleteBuffer(buf);
      gl.deleteVertexArray(vao);
      gl.deleteProgram(prog);
    },
  };
};
